import { i18n } from "@/shared/lib/i18n";

export type RootErrorKind =
	| "backend-connection"
	| "frontend-module"
	| "unknown";

export interface RootErrorDiagnosis {
	checks: readonly string[];
	description: string;
	devChecks: readonly string[];
	kind: RootErrorKind;
	title: string;
}

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
	/failed to fetch dynamically imported module/i,
	/error loading dynamically imported module/i,
	/importing a module script failed/i,
	/loading chunk [\w-]+ failed/i,
	/failed to load module script/i,
] as const;

const BACKEND_CONNECTION_ERROR_PATTERNS = [
	/^failed to fetch$/i,
	/networkerror when attempting to fetch resource/i,
	/network request failed/i,
	/load failed/i,
	/err_connection_refused/i,
	/econnrefused/i,
	/backend connection/i,
	/fetch failed/i,
] as const;

export function getRootErrorMessage(
	error: unknown,
	isLocalized = false,
): string {
	if (error instanceof Error && error.message.trim().length > 0) {
		return error.message;
	}

	if (typeof error === "string" && error.trim().length > 0) {
		return error;
	}

	return i18n.getMessage("undo_unknown_error", isLocalized);
}

export function getRootErrorDetails(
	error: unknown,
	isLocalized = false,
): string {
	if (error instanceof Error) {
		return error.stack ?? error.message;
	}

	return getRootErrorMessage(error, isLocalized);
}

export function diagnoseRootError(
	error: unknown,
	isLocalized = false,
): RootErrorDiagnosis {
	const message = getRootErrorMessage(error, isLocalized);

	if (DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
		return {
			kind: "frontend-module",
			title: i18n.getMessage("wgt_root_error_module_title", isLocalized),
			description: i18n.getMessage(
				"wgt_root_error_module_description",
				isLocalized,
			),
			checks: [
				i18n.getMessage("wgt_root_error_module_check_1", isLocalized),
				i18n.getMessage("wgt_root_error_module_check_2", isLocalized),
			],
			devChecks: [
				i18n.getMessage("wgt_root_error_module_dev_check_1", isLocalized),
				i18n.getMessage("wgt_root_error_module_dev_check_2", isLocalized),
			],
		};
	}

	if (
		BACKEND_CONNECTION_ERROR_PATTERNS.some((pattern) => pattern.test(message))
	) {
		return {
			kind: "backend-connection",
			title: i18n.getMessage("wgt_root_error_backend_title", isLocalized),
			description: i18n.getMessage(
				"wgt_root_error_backend_description",
				isLocalized,
			),
			checks: [
				i18n.getMessage("wgt_root_error_backend_check_1", isLocalized),
				i18n.getMessage("wgt_root_error_backend_check_2", isLocalized),
			],
			devChecks: [
				i18n.getMessage("wgt_root_error_backend_dev_check_1", isLocalized),
				i18n.getMessage("wgt_root_error_backend_dev_check_2", isLocalized),
			],
		};
	}

	return {
		kind: "unknown",
		title: i18n.getMessage("wgt_root_error_unknown_title", isLocalized),
		description: i18n.getMessage(
			"wgt_root_error_unknown_description",
			isLocalized,
		),
		checks: [
			i18n.getMessage("wgt_root_error_unknown_check_1", isLocalized),
			i18n.getMessage("wgt_root_error_unknown_check_2", isLocalized),
		],
		devChecks: [],
	};
}
